<template>
  <div class="control-panel__userAccess">
    <div class="control-panel__addUser">
      <b-button variant="primary" size="sm" @click="showCreateUserModal">
        <icon name="plus" />
        <span>Create user</span>
      </b-button>
    </div>
    <b-form-group label="Search user:">
      <b-form-input v-model="emailSearchKey" />
    </b-form-group>

    <b-pagination
      v-if="usersReady.length > perPage"
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      aria-controls="my-table"
    ></b-pagination>

    <b-table
      :items="usersReady"
      :fields="fields"
      :busy="!loaded"
      class="mt-3"
      outlined
      hover
      :per-page="perPage"
      :current-page="currentPage"
    >
      <template v-slot:table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Loading...</strong>
        </div>
      </template>

      <template v-slot:cell(email)="row">
        <span v-if="row.item.email">{{ row.item.email }}</span>
        <i v-else style="color: #bbb;">(anonymous)</i>
      </template>

      <template v-slot:cell(isAdmin)="row">
        <b-form-checkbox
          :checked="isAdmin(row.item.uid)"
          :disabled="userId === row.item.uid"
          @change="toggleAdminPermission(row.item.uid)"
        />
      </template>

      <template v-slot:cell(dAccess)="row">
        <b-button variant="primary" size="sm" @click="showDashboardsAccessModal(row.item)">
          <icon name="pencil" />
          <span>Edit</span>
        </b-button>
      </template>

      <template v-slot:cell(resetPass)="row">
        <a
          href="#"
          @click.prevent="showResetPassForm(row.item.uid)"
          :class="[`js-d-access-reset-pass-link-${row.item.uid}`]"
        >reset</a>
        <b-input-group
          size="sm"
          :class="[`js-d-access-reset-pass-form-${row.item.uid}`]"
          style="display: none;"
        >
          <b-form-input
            :class="`js-d-access-reset-pass-input-${row.item.uid}`"
            @keydown.enter="resetPass(row.item.uid)"
            @keydown.escape.stop="cancelResetPass(row.item.uid)"
            placeholder="Press Escape to cancel"
            :disabled="passResInProcess"
          />
          <b-input-group-append>
            <b-button
              size="sm"
              text="Button"
              variant="success"
              @click="resetPass(row.item.uid)"
              :disabled="passResInProcess"
            >Save</b-button>
          </b-input-group-append>
        </b-input-group>
      </template>
    </b-table>

    <b-pagination
      v-if="usersReady.length > perPage"
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      aria-controls="my-table"
    ></b-pagination>

    <CreateUserModal
      :users="users"
      :dashboards="dashboards"
      ref="createUserModal"
      @created="getUserList"
    />
    <DashboardsAccessModal :dashboards="dashboards" :access="access" ref="dashboardAccessModal" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { find } from 'lodash'
// services
import user from '@/services/user'
import db from '@/services/db'
import auth from '@/services/auth'
// components
import CreateUserModal from './modals/CreateUser'
import DashboardsAccessModal from './modals/DashboardsAccess'

export default {
  name: 'ControlPanel_UserAccess',
  components: {
    CreateUserModal,
    DashboardsAccessModal,
  },
  data() {
    return {
      loaded: false,
      perPage: 20,
      currentPage: 1,
      users: null,
      access: null,
      dashboards: null,
      fields: [
        { key: 'email', label: 'Email' },
        { key: 'createdOn', label: 'Created on' },
        { key: 'lastSignIn', label: 'Last sign in' },
        { key: 'isAdmin', label: 'Is Admin' },
        { key: 'dAccess', label: 'Dashboard Access' },
        { key: 'resetPass', label: 'Reset Password' },
      ],
      emailSearchKey: null,
      passResInProcess: false,
    }
  },
  computed: {
    ...mapState(['userId']),
    usersReady() {
      if (!this.users || !this.access) return []

      const users = []

      for (let idx in this.users) {
        const user = this.users[idx]

        if (typeof user !== 'object') continue

        const userReady = {
          uid: user.uid,
          email: user.email,
          createdOn: user.metadata.creationTime,
          lastSignIn: user.metadata.lastSignInTime,
        }

        if (this.userId === user.uid) {
          userReady['_rowVariant'] = 'success'
        }

        if (
          !this.emailSearchKey ||
          (user.email && user.email.toLowerCase().includes(this.emailSearchKey))
        ) {
          users.push(userReady)
        }
      }

      return users
    },
    rows() {
      return this.usersReady.length
    },
  },
  methods: {
    isAdmin(uid) {
      return this.access.Admin[uid] && this.access.Admin[uid].all ? true : false
    },
    getUserEmail(uid) {
      const user = find(this.users, { uid })
      return user.email || uid
    },
    showCreateUserModal() {
      this.$refs.createUserModal.open()
    },
    toggleAdminPermission(uid) {
      const email = this.getUserEmail(uid)

      if (this.isAdmin(uid)) {
        delete this.access.Admin[uid]

        db.access.removeUserAdminRight(uid).then(() => {
          this.$bvToast.toast(
            `Admin permission has been deleted for "${email}".`,
            {
              title: 'Success!',
              variant: 'success',
              solid: true,
              toaster: 'b-toaster-top-center',
              autoHideDelay: 2500,
            }
          )
        })
      } else {
        this.access.Admin[uid] = { all: true }

        db.access.setUserAsAdmin(uid).then(() => {
          this.$bvToast.toast(
            `Admin permission has been granted for "${email}".`,
            {
              title: 'Success!',
              variant: 'success',
              solid: true,
              toaster: 'b-toaster-top-center',
              autoHideDelay: 2500,
            }
          )
        })
      }
    },
    // reset password
    showResetPassForm(uid) {
      $(`.js-d-access-reset-pass-link-${uid}`).hide()
      $(`.js-d-access-reset-pass-form-${uid}`).show()
      $(`.js-d-access-reset-pass-input-${uid}`).focus()
    },
    async resetPass(uid) {
      const newPass = $(`.js-d-access-reset-pass-input-${uid}`).val()

      if (!newPass) return

      this.passResInProcess = true

      const idToken = await auth.getIdToken()
      const data = {
        idToken,
        uid: uid,
        dashboard: this.currentDash,
        pass: newPass,
      }

      user.resetUserPass(data).then(() => {
        $(`.js-d-access-reset-pass-link-${uid}`).show()
        $(`.js-d-access-reset-pass-form-${uid}`).hide()
        $(`.js-d-access-reset-pass-input-${uid}`).val('')

        const email = this.getUserEmail(uid)

        this.$bvToast.toast(`User password has been reset for "${email}".`, {
          title: 'Success!',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-top-center',
          autoHideDelay: 2500,
        })

        this.passResInProcess = false
      })
    },
    cancelResetPass(uid) {
      $(`.js-d-access-reset-pass-link-${uid}`).show()
      $(`.js-d-access-reset-pass-form-${uid}`).hide()
      $(`.js-d-access-reset-pass-input-${uid}`).val('')
    },
    getUserList() {
      return new Promise(resolve => {
        user.getUserList().then(users => {
          this.users = users || {}
          resolve()
        })
      })
    },
    getAccess() {
      return new Promise(resolve => {
        db.access.read(access => {
          this.access = access
          resolve()
        })
      })
    },
    showDashboardsAccessModal(item) {
      const data = {
        uid: item.uid,
        email: item.email,
      }

      this.$refs.dashboardAccessModal.open(data)
    },
    saveDashboardAccess(data) {
      const { dId, uid } = data

      db.access.saveDashboardAccess(data).then(() => {
        const message = `Access to "${
          this.dashboards[dId].name
        }" dashboard has been granted for user "${this.getUserEmail(uid)}"`

        this.$bvToast.toast(message, {
          title: 'Success!',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-top-center',
          autoHideDelay: 2500,
        })
      })

      this.getAccess()
    },
    removeDashboardAccess(data) {
      const { dId, uid } = data

      db.access.removeDashboardAccess(data).then(() => {
        const message = `Access to "${
          this.dashboards[dId].name
        }" dashboard has been deleted for user "${this.getUserEmail(uid)}"`

        this.$bvToast.toast(message, {
          title: 'Success!',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-top-center',
          autoHideDelay: 2500,
        })
      })

      this.getAccess()
    },
  },
  created() {
    const queue = [
      {
        name: 'getUsers',
        promise: this.getUserList(),
      },
      {
        name: 'getAccess',
        promise: this.getAccess(),
      },
      {
        name: 'getDashboards',
        promise: new Promise(resolve => {
          db.dashboards.readAll().then(dashboards => {
            this.dashboards = dashboards || {}
            resolve()
          })
        }),
      },
    ]

    // exec queue
    this.$loadingQueue(queue, () => {
      this.loaded = true
    })
  },
}
</script>

<style lang="scss">
.control-panel {
  &__userAccess {
    position: relative;
    padding-top: 10px;
    .table {
      background: white;
    }
  }
  &__addUser {
    position: absolute;
    right: 0;
    top: 9px;
  }
}
</style>
